/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import fetchData from "../../functions/fetchData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import CardComponent from "../../components/cards/CardComponent";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import { FaPrint, FaFileInvoice, FaMoneyCheckAlt, FaUserTie, FaCalendarAlt, FaWallet, FaCreditCard } from "react-icons/fa";
import "./paymentVouchers.css";

export default function PaymentVouchers() {
    const [suppliers, setsuppliers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState("");
    const [bankAccountId, setBankAccountId] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [remarks, setRemarks] = useState("");
    const [balanceAfterPayment, setBalanceAfterPayment] = useState(0);
    const [data, setData] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState("All");
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    
    // Payment modes with bank account requirement
    const paymentModes = [
        { value: "Cash", label: "Cash", requiresBank: false },
        { value: "Bank", label: "Bank Transfer", requiresBank: true },
        { value: "Credit_Card", label: "Credit Card", requiresBank: true },
        { value: "UPI", label: "UPI", requiresBank: true },
        { value: "Cheque", label: "Cheque", requiresBank: true },
        { value: "Online", label: "Online Payment", requiresBank: true }
    ];
    
    // Fetch suppliers, bank accounts and company info when component loads
    useEffect(() => {
        fetchData("suppliers", setsuppliers, "id", {});
        fetchBankAccounts();
        fetchCompanyInfo();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            const res = await axios.get(`/bank-accounts`, getHeaders());
            if (res.data.success) {
                setBankAccounts(res.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const companyData = await fetchData("company_profile", null, "id", {});
            if (companyData && companyData.length > 0) {
                setCompanyInfo(companyData[0]);
            }
        } catch (error) {
            console.error("Error fetching company info:", error);
        }
    };

    const getOutStandingBalance = async (e) => {
        const customerId = e.target.value;
        setSelectedCustomer(customerId); // Update selected customer state

        if (!customerId) {
            setOutstandingAmount(0);
            return;
        }

        try {
            const res = await axios.get(`/supplier-ledger-balance/${customerId}`, getHeaders());
            if (res.data.success) {
                // Use the balance from the API response
                const balance = res.data.data.balance || 0;
                setOutstandingAmount(Math.abs(balance)); // Show absolute value as outstanding amount
            } else {
                toast.error("Failed to fetch supplier outstanding balance");
            }
        } catch (error) {
            console.error("Error fetching outstanding balance:", error);
            toast.error("Error fetching outstanding balance.");
        }
    };

    useEffect(() => {
        const remaining = outstandingAmount - (parseFloat(paymentAmount) || 0);
        setBalanceAfterPayment(remaining >= 0 ? remaining : 0);
    }, [paymentAmount, outstandingAmount]);



    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCustomer || !paymentAmount || !paymentMode) {
            toast.error("Please fill in all required fields");
            return;
        }

        // Check if bank account is required but not selected
        const selectedMode = paymentModes.find(mode => mode.value === paymentMode);
        if (selectedMode && selectedMode.requiresBank && !bankAccountId) {
            toast.error('Please select bank account for this payment mode');
            return;
        }

        const paymentData = {
            supplier_id: selectedCustomer,
            amount: parseFloat(paymentAmount) || 0,
            reference_number: referenceNumber,
            payment_mode: paymentMode,
            bank_account_id: bankAccountId || null,
            description: remarks,
            voucher_date: new Date().toISOString().split('T')[0]
        };

        setLoading(true);
        try {
            const res = await axios.post(`/payment-vouchers`, paymentData, getHeaders());

            if (res.data.success) {
                toast.success("Payment voucher created successfully!");
                await fetchVoucherData();
                
                // Reset form
                setSelectedCustomer("");
                setPaymentAmount("");
                setPaymentMode("");
                setBankAccountId("");
                setReferenceNumber("");
                setRemarks("");
                setOutstandingAmount(0);
                setBalanceAfterPayment(0);
            }
        } catch (error) {
            console.error("Error saving payment:", error);
            toast.error(`Error creating payment voucher: ${error?.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintPaymentVoucher = async (voucherData) => {
        try {
            setLoading(true);
            
            // Get supplier details
            const supplier = suppliers.find(s => s.id === parseInt(voucherData.supplier_id));
            const supplierName = supplier ? supplier.supplier_name : 'N/A';
            const supplierAddress = supplier ? supplier.address || 'N/A' : 'N/A';
            const supplierPhone = supplier ? supplier.phone || 'N/A' : 'N/A';
            
            // Format date and time
            const paymentDate = new Date(voucherData.payment_date);
            const formattedDate = paymentDate.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });

            // Convert amount to Thai Baht text
            const amountValue = parseFloat(voucherData.amount || 0);
            const amountInText = numberToThaiText(amountValue);

            // Process logo - handle blob/base64/buffer
            let logoSrc = '';
            if (companyInfo.logo) {
                try {
                    let logoData;
                    
                    // Check if it's a Buffer-like object (from database BLOB)
                    if (companyInfo.logo.type === 'Buffer' && Array.isArray(companyInfo.logo.data)) {
                        // Convert Buffer array to base64
                        const uint8Array = new Uint8Array(companyInfo.logo.data);
                        let binary = '';
                        for (let i = 0; i < uint8Array.byteLength; i++) {
                            binary += String.fromCharCode(uint8Array[i]);
                        }
                        logoData = btoa(binary);
                        logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                    } 
                    // Check if it's already a base64 string
                    else if (typeof companyInfo.logo === 'string') {
                        logoData = companyInfo.logo;
                        
                        if (logoData.startsWith('data:image')) {
                            // Already base64 with prefix
                            logoSrc = logoData;
                        } else if (logoData.startsWith('blob:')) {
                            // Blob URL
                            logoSrc = logoData;
                        } else {
                            // Assume it's base64 without prefix
                            logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                        }
                    }
                } catch (error) {
                    console.error('Error processing logo:', error);
                    logoSrc = ''; // Fallback to no logo
                }
            }

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>Payment Voucher</title>
                    <style>
                        @page {
                            size: A4;
                            margin: 10mm 5mm;
                        }
                        
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        
                        body {
                            font-family: Arial, 'Sarabun', sans-serif;
                            font-size: 10pt;
                            line-height: 1.4;
                            color: #000;
                            background: white;
                        }
                        
                        .voucher-container {
                            max-width: 210mm;
                            margin: 0 auto;
                            padding: 8mm;
                            border: 2px solid #333;
                            background: white;
                        }
                        
                        .header-section {
                            display: flex;
                            align-items: flex-start;
                            margin-bottom: 15px;
                            padding-bottom: 15px;
                            border-bottom: 2px solid #ddd;
                            background: white;
                            padding: 15px;
                        }
                        
                        .logo-section {
                            flex: 0 0 120px;
                            margin-right: 15px;
                        }
                        
                        .logo-section img {
                            max-width: 100px;
                            max-height: 80px;
                            object-fit: contain;
                        }
                        
                        .company-info {
                            flex: 1;
                        }
                        
                        .company-name {
                            font-size: 14pt;
                            font-weight: bold;
                            margin-bottom: 3px;
                            color: #000;
                        }
                        
                        .company-details {
                            font-size: 8pt;
                            line-height: 1.3;
                            color: #333;
                        }
                        
                        .voucher-title {
                            text-align: center;
                            font-size: 16pt;
                            font-weight: bold;
                            margin: 15px 0;
                            padding: 10px;
                            background: linear-gradient(135deg, #F59D22FF 0%, #F59D22FF 100%);
                            color: white;
                            border-radius: 5px;
                            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
                        }
                        
                        .info-section {
                            margin-bottom: 15px;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border-left: 4px solid #ddd;
                        }
                        
                        .info-row {
                            display: flex;
                            margin-bottom: 5px;
                            font-size: 10pt;
                        }
                        
                        .info-label {
                            font-weight: bold;
                            min-width: 100px;
                            color: #000;
                        }
                        
                        .info-value {
                            flex: 1;
                        }
                        
                        .right-align {
                            text-align: right;
                            flex: 1;
                            color: #000;
                            font-weight: bold;
                        }
                        
                        .amount-in-words {
                            margin: 10px 0;
                            padding: 12px;
                            background: #fffef0;
                            border-left: 4px solid #ddd;
                            font-weight: bold;
                            font-size: 10pt;
                            border-radius: 5px;
                        }
                        
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 15px 0;
                            font-size: 9pt;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        
                        table, th, td {
                            border: 1px solid #B0BEC5;
                        }
                        
                        th {
                            background: #f5f5f5;
                            padding: 10px;
                            text-align: center;
                            font-weight: bold;
                            color: #000;
                        }
                        
                        td {
                            padding: 8px;
                            background: white;
                        }
                        
                        .text-center {
                            text-align: center;
                        }
                        
                        .text-right {
                            text-align: right;
                        }
                        
                        .total-row {
                            font-weight: bold;
                            background: #f0f0f0;
                            color: #000;
                        }
                        
                        .signature-section {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 40px;
                            font-size: 9pt;
                        }
                        
                        .signature-box {
                            text-align: center;
                            flex: 0 0 30%;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border: 1px solid #ddd;
                        }
                        
                        .signature-line {
                            border-top: 1px solid #000;
                            margin-top: 50px;
                            padding-top: 8px;
                            font-weight: bold;
                            color: #000;
                        }
                        
                        .footer-note {
                            margin-top: 20px;
                            font-size: 8pt;
                            color: #666;
                            text-align: center;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border-top: 1px solid #ddd;
                        }
                        
                        @media print {
                            body {
                                margin: 0;
                                padding: 0;
                            }
                            
                            .voucher-container {
                                border: 2px solid #000;
                                page-break-inside: avoid;
                                box-shadow: none;
                            }
                            
                            .header-section {
                                background: #f0f0f0 !important;
                                border-bottom: 2px solid #000 !important;
                            }
                            
                            .voucher-title {
                                background: #e0e0e0 !important;
                                color: #000 !important;
                            }
                            
                            .info-section {
                                background: #f5f5f5 !important;
                                border-left: 3px solid #000 !important;
                            }
                            
                            .amount-in-words {
                                background: #fffef0 !important;
                                border-left: 3px solid #000 !important;
                            }
                            
                            th {
                                background: #e0e0e0 !important;
                                color: #000 !important;
                            }
                            
                            .total-row {
                                background: #f0f0f0 !important;
                            }
                            
                            .signature-box {
                                background: #f5f5f5 !important;
                                border: 1px solid #000 !important;
                            }
                            
                            .signature-line {
                                border-top: 1px solid #000 !important;
                                color: #000 !important;
                            }
                            
                            .footer-note {
                                background: #f5f5f5 !important;
                                border-left: 2px solid #000 !important;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="voucher-container">
                        <!-- Header Section -->
                        <div class="header-section">
                            ${logoSrc ? `
                            <div class="logo-section">
                                <img src="${logoSrc}" alt="${companyInfo.name || 'Company Logo'}" />
                            </div>
                            ` : ''}
                            <div class="company-info">
                                <div class="company-name">${companyInfo.name || 'COMPANY NAME'}</div>
                                <div class="company-details">
                                    <strong>Reg No:</strong> ${companyInfo.tax_id || 'XXXXXXXXXXXXX'}<br>
                                    <strong>Address:</strong> ${companyInfo.address || 'Company Address'}${companyInfo.city ? `, ${companyInfo.city}` : ''}${companyInfo.state ? `, ${companyInfo.state}` : ''}${companyInfo.zip_code ? `, ${companyInfo.zip_code}` : ''}<br>
                                    <strong>Tel:</strong> ${companyInfo.phone_number || 'XXX-XXX-XXXX'}
                                    ${companyInfo.email ? ` | <strong>Email:</strong> ${companyInfo.email}` : ''}<br>
                                    ${companyInfo.website ? `<strong>Website:</strong> ${companyInfo.website}` : ''}
                                </div>
                            </div>
                        </div>
                        
                        <!-- Title -->
                        <div class="voucher-title">PAYMENT VOUCHER</div>
                        
                        <!-- Voucher Info -->
                        <div class="info-section">
                            <div style="display: flex; justify-content: space-between;">
                                <div style="flex: 1;">
                                    <div class="info-row">
                                        <span class="info-label">PAY TO</span>
                                        <span class="info-value">${supplierName}</span>
                                    </div>
                                    <div class="info-row">
                                        <span style="min-width: 100px;"></span>
                                        <span class="info-value">${supplierAddress}</span>
                                    </div>
                                    ${supplierPhone && supplierPhone !== 'N/A' ? `
                                    <div class="info-row">
                                        <span style="min-width: 100px;"></span>
                                        <span class="info-value">${supplierPhone}</span>
                                    </div>
                                    ` : ''}
                                </div>
                                <div style="flex: 0 0 200px; text-align: right;">
                                    <div style="margin-bottom: 5px;">
                                        <strong>Voucher No.:</strong> PV-${String(voucherData.id).padStart(6, '0')}
                                    </div>
                                    <div>
                                        <strong>Date:</strong> ${formattedDate}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Amount in Words -->
                        <div class="amount-in-words">
                            PAY THE SUM OF<br>
                            <span style="text-transform: uppercase;">${amountInText}</span>
                        </div>
                        
                        <!-- Payment Details Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 20%;">Payment By</th>
                                    <th style="width: 25%;">Cheque No.</th>
                                    <th style="width: 55%;">Payment Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="text-center">${getPaymentModeInThai(voucherData.payment_mode)}</td>
                                    <td class="text-center">${voucherData.reference_id || '-'}</td>
                                    <td class="text-right">${(() => {
                                        const amount = parseFloat(voucherData.amount || 0);
                                        return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    })()}</td>
                                </tr>
                            </tbody>
                        </table>
                        
                        <!-- Details Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 15%;">Acc. No.</th>
                                    <th style="width: 65%;">Description</th>
                                    <th style="width: 20%;">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="text-center">400-S001</td>
                                    <td>Payment to ${supplierName}${voucherData.remarks ? ` - ${voucherData.remarks}` : ''}</td>
                                    <td class="text-right">${(() => {
                                        const amount = parseFloat(voucherData.amount || 0);
                                        return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    })()}</td>
                                </tr>
                                <tr>
                                    <td colspan="2" class="text-right total-row">TOTAL:</td>
                                    <td class="text-right total-row">${(() => {
                                        const amount = parseFloat(voucherData.amount || 0);
                                        return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    })()}</td>
                                </tr>
                            </tbody>
                        </table>
                        
                        <!-- Footer Note -->
                        <div class="footer-note">
                            <div style="text-align: center; margin-bottom: 5px;">
                                <strong>This payment voucher was prepared by StoreHub Accounting System developed by Cloudnet Softwares</strong>
                            </div>
                            <div style="font-size: 8pt; text-align: center; color: #666;">
                                เอกสารนี้จัดทำขึ้นตามข้อกำหนดของกฎหมายภาษีอากรไทย พ.ศ. 2560<br>
                                <em>This document is prepared in accordance with Thai Revenue Code B.E. 2560 (2017) and Accounting Standards</em>
                            </div>
                        </div>
                        
                        <!-- Signature Section -->
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line">
                                    APPROVED BY
                                </div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line">
                                    RECEIVED BY
                                </div>
                            </div>
                        </div>
                    </div>
                </body>
                </html>
            `;

            // Open print window
            const printWindow = window.open("", "_blank");
            printWindow.document.write(printContent);
            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                }, 250);
            };

            setLoading(false);
        } catch (error) {
            console.error("Error printing voucher:", error);
            toast.error("Error generating payment voucher");
            setLoading(false);
        }
    };

    // Helper function to format currency
    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '฿ 0.00';
        return `฿ ${parseFloat(amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Helper function to convert payment mode to Thai
    const getPaymentModeInThai = (mode) => {
        const modes = {
            'Cash': 'เงินสด / Cash',
            'Bank': 'โอนเงินผ่านธนาคาร / Bank Transfer',
            'Cheque': 'เช็ค / Cheque',
            'Online': 'ชำระออนไลน์ / Online Payment'
        };
        return modes[mode] || mode;
    };

    // Helper function to convert number to Thai text
    const numberToThaiText = (number) => {
        const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
        const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];
        
        if (number === 0) return 'ศูนย์บาทถ้วน';
        
        const [intPart, decPart] = number.toFixed(2).split('.');
        let result = '';
        
        // Convert integer part
        const intStr = intPart.toString();
        const len = intStr.length;
        
        for (let i = 0; i < len; i++) {
            const digit = parseInt(intStr[i]);
            const position = len - i - 1;
            
            if (digit === 0) continue;
            
            if (position === 1 && digit === 1) {
                result += 'สิบ';
            } else if (position === 1 && digit === 2) {
                result += 'ยี่สิบ';
            } else if (position === 0 && digit === 1 && len > 1) {
                result += 'เอ็ด';
            } else {
                result += digits[digit] + positions[position];
            }
        }
        
        result += 'บาท';
        
        // Convert decimal part
        if (parseInt(decPart) === 0) {
            result += 'ถ้วน';
        } else {
            const satangStr = decPart.toString();
            let satangText = '';
            
            for (let i = 0; i < satangStr.length; i++) {
                const digit = parseInt(satangStr[i]);
                const position = satangStr.length - i - 1;
                
                if (digit === 0 && position === 1) continue;
                
                if (position === 1 && digit === 1) {
                    satangText += 'สิบ';
                } else if (position === 1 && digit === 2) {
                    satangText += 'ยี่สิบ';
                } else if (position === 0 && digit === 1 && satangStr.length > 1) {
                    satangText += 'เอ็ด';
                } else {
                    satangText += digits[digit];
                    if (position === 1) satangText += 'สิบ';
                }
            }
            
            result += satangText + 'สตางค์';
        }
        
        return result;
    };
    const fetchVoucherData = async () => {
        try {
            setLoading(true);
            const vouchers = await fetchData("payment_vouchers", null, "id", {});
            setData(vouchers || []);
        } catch (error) {
            console.error("Error fetching payment vouchers:", error);
            toast.error("Failed to load payment vouchers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAndSetData = async () => {
            try {
                await fetchVoucherData();
            } catch (error) {
                console.error("Error in useEffect:", error);
            }
        };

        fetchAndSetData();
    }, []);

    // Filter data based on search and filter mode
    const filteredData = data.filter(item => {
        const matchesSearch = 
            item.supplier_id?.toString().includes(searchTerm) ||
            item.amount?.toString().includes(searchTerm) ||
            item.reference_id?.toString().includes(searchTerm);
        
        const matchesFilter = filterMode === "All" || item.payment_mode === filterMode;
        
        return matchesSearch && matchesFilter;
    });

    // Pagination calculations
    const totalRecords = filteredData.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterMode, recordsPerPage]);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        
        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= maxPagesToShow; i++) {
                    pages.push(i);
                }
            } else if (currentPage >= totalPages - 2) {
                for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                    pages.push(i);
                }
            }
        }
        return pages;
    };

    return (
        <Layout>
            <Header title="Payment Vouchers / ใบสำคัญจ่าย" />
            <ToastContainer />

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: '30px', 
                        borderRadius: '10px',
                        textAlign: 'center'
                    }}>
                        <div className="spinner-border text-warning mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div>Processing...</div>
                    </div>
                </div>
            )}

            <div className="row">
                {/* Left Panel - Payment Form */}
                <div className="col-lg-4 col-sm-12 mb-4">
                    <CardComponent title="Create Payment Voucher" headerColor="warning">
                        <form onSubmit={handleSubmit} className="payment-form">
                            {/* Supplier Selection */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaUserTie className="me-2" />
                                    Supplier Name <span className="text-danger">*</span>
                                </label>
                                <select 
                                    value={selectedCustomer} 
                                    onChange={getOutStandingBalance} 
                                    className="form-control form-select"
                                    required
                                >
                                    <option value="">-- Select Supplier --</option>
                                    {suppliers.map((supplier) => (
                                        <option key={supplier.id} value={supplier.id}>
                                            {supplier.supplier_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Outstanding Amount */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaMoneyCheckAlt className="me-2" />
                                    Outstanding Amount
                                </label>
                                <input 
                                    type="text" 
                                    value={`฿ ${outstandingAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`} 
                                    readOnly 
                                    className="form-control bg-light"
                                />
                            </div>

                            {/* Payment Mode */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaWallet className="me-2" />
                                    Payment Mode <span className="text-danger">*</span>
                                </label>
                                <select 
                                    value={paymentMode} 
                                    onChange={(e) => {
                                        setPaymentMode(e.target.value);
                                        setBankAccountId(''); // Reset bank account when mode changes
                                    }} 
                                    className="form-control form-select"
                                    required
                                >
                                    <option value="">-- Select Payment Mode --</option>
                                    {paymentModes.map(mode => (
                                        <option key={mode.value} value={mode.value}>
                                            {mode.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Bank Account Selection - Only show when bank is required */}
                            {paymentMode && paymentModes.find(mode => mode.value === paymentMode)?.requiresBank && (
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <FaCreditCard className="me-2" />
                                        Bank Account <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        value={bankAccountId}
                                        onChange={(e) => setBankAccountId(e.target.value)}
                                        className="form-control form-select"
                                        required
                                    >
                                        <option value="">Select Bank Account</option>
                                        {bankAccounts.filter(acc => acc.is_active).map(account => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_name} ({account.bank_name}) - {formatCurrency(account.current_balance)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Amount Paid */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaMoneyCheckAlt className="me-2" />
                                    Amount Paid <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    className="form-control"
                                    placeholder="0.00"
                                    required
                                />
                            </div>

                            {/* Reference Number */}
                            <div className="form-group mb-3">
                                <label className="form-label">
                                    Reference Number (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    className="form-control"
                                    placeholder="Enter reference number"
                                />
                            </div>

                            {/* Remarks */}
                            <div className="form-group mb-3">
                                <label className="form-label">Remarks (Optional)</label>
                                <textarea 
                                    value={remarks} 
                                    onChange={(e) => setRemarks(e.target.value)} 
                                    className="form-control"
                                    rows="3"
                                    placeholder="Enter any additional notes"
                                />
                            </div>

                            {/* Balance After Payment */}
                            <div className="form-group mb-4">
                                <label className="form-label d-flex align-items-center">
                                    <FaMoneyCheckAlt className="me-2" />
                                    Balance After Payment
                                </label>
                                <input 
                                    type="text" 
                                    value={`฿ ${balanceAfterPayment.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`} 
                                    readOnly 
                                    className={`form-control ${balanceAfterPayment > 0 ? 'bg-warning-custom' : 'bg-success-custom text-white'}`}
                                    style={{ fontWeight: 'bold' }}
                                />
                            </div>

                            {/* Submit Button */}
                            <button 
                                type="submit" 
                                className="btn btn-warning-gradient btn-lg w-100"
                                disabled={loading}
                            >
                                <FaFileInvoice className="me-2" />
                                {loading ? 'Processing...' : 'Save Payment'}
                            </button>
                        </form>
                    </CardComponent>
                </div>

                {/* Right Panel - Payment History Table */}
                <div className="col-lg-8 col-md-8 col-sm-12 mb-4">
                    <CardComponent title="Payment History" headerColor="warning">
                        {/* Search and Filter Section */}
                        <div className="row mb-3">
                            <div className="col-md-3 mb-2">
                                <select
                                    className="form-control form-select"
                                    value={recordsPerPage}
                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                >
                                    <option value={5}>5 records</option>
                                    <option value={10}>10 records</option>
                                    <option value={25}>25 records</option>
                                    <option value={50}>50 records</option>
                                    <option value={100}>100 records</option>
                                </select>
                            </div>
                            <div className="col-md-5 mb-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="🔍 Search by Supplier ID, Amount, Reference ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4 mb-2">
                                <select
                                    className="form-control form-select"
                                    value={filterMode}
                                    onChange={(e) => setFilterMode(e.target.value)}
                                >
                                    <option value="All">All Payment Modes</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Bank">Bank</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Online">Online</option>
                                </select>
                            </div>
                        </div>

                        {/* Table */}
                        {filteredData.length === 0 ? (
                            <div className="alert alert-warning-custom text-center">
                                <FaFileInvoice size={50} className="mb-3" />
                                <p>No payment records found</p>
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <table className="table table-hover table-bordered">
                                    <thead className="table-warning-dark sticky-top">
                                        <tr>
                                            <th>Voucher ID</th>
                                            <th>Supplier</th>
                                            <th>Date</th>
                                            <th>Mode</th>
                                            <th>Amount</th>
                                            <th>Ref ID</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecords.map((item) => {
                                            const supplier = suppliers.find(s => s.id === parseInt(item.supplier_id));
                                            const supplierName = supplier ? supplier.supplier_name : `Supplier #${item.supplier_id}`;
                                            
                                            return (
                                                <tr key={item.id}>
                                                    <td className="fw-bold">PV-{item.id}</td>
                                                    <td>{supplierName}</td>
                                                    <td>{new Date(item.payment_date).toLocaleString('th-TH')}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            item.payment_mode === 'Cash' ? 'bg-success' :
                                                            item.payment_mode === 'Bank' ? 'bg-primary' :
                                                            item.payment_mode === 'Cheque' ? 'bg-warning' :
                                                            'bg-info'
                                                        }`}>
                                                            {item.payment_mode}
                                                        </span>
                                                    </td>
                                                    <td className="fw-bold">
                                                        ฿ {(() => {
                                                            const amount = item.amount || 0;
                                                            return parseFloat(amount).toLocaleString('th-TH', { minimumFractionDigits: 2 });
                                                        })()}
                                                    </td>
                                                    <td>{item.reference_id || '-'}</td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-warning"
                                                            onClick={() => handlePrintPaymentVoucher(item)}
                                                            title="Print Payment Voucher"
                                                        >
                                                            <FaPrint /> Print
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination Controls */}
                        {filteredData.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-3 mb-3" style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                padding: '10px 15px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                <div className="text-muted" style={{ fontSize: '13px', fontWeight: '500' }}>
                                    Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, totalRecords)} of {totalRecords} records
                                </div>
                                <div>
                                    <nav>
                                        <ul className="pagination mb-0" style={{ gap: '4px' }}>
                                            {/* First Button */}
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(1)}
                                                    disabled={currentPage === 1}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === 1 ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === 1 ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    First
                                                </button>
                                            </li>
                                            
                                            {/* Previous Button */}
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === 1 ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === 1 ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Prev
                                                </button>
                                            </li>
                                            
                                            {/* Page Numbers */}
                                            {getPageNumbers().map(pageNum => (
                                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                    <button 
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: '12px',
                                                            borderRadius: '5px',
                                                            border: '1px solid #dee2e6',
                                                            background: currentPage === pageNum 
                                                                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' 
                                                                : 'white',
                                                            color: currentPage === pageNum ? 'white' : '#495057',
                                                            fontWeight: currentPage === pageNum ? '600' : '500',
                                                            minWidth: '32px',
                                                            transition: 'all 0.3s ease',
                                                            boxShadow: currentPage === pageNum ? '0 2px 8px rgba(240, 147, 251, 0.4)' : 'none'
                                                        }}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            ))}
                                            
                                            {/* Next Button */}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === totalPages ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === totalPages ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                            
                                            {/* Last Button */}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === totalPages ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === totalPages ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Last
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        )}

                        {/* Summary */}
                        <div className="mt-3 p-3 bg-light-orange border rounded">
                            <div className="row">
                                <div className="col-md-4 text-center">
                                    <h6>Total Records</h6>
                                    <h4 className="text-warning-dark">{filteredData.length}</h4>
                                </div>
                                <div className="col-md-4 text-center">
                                    <h6>Total Amount</h6>
                                    <h4 className="text-danger">
                                        ฿ {filteredData.reduce((sum, item) => {
                                            const amount = item.amount || 0;
                                            return sum + parseFloat(amount);
                                        }, 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </h4>
                                </div>
                                <div className="col-md-4 text-center">
                                    <h6>Latest Payment</h6>
                                    <h4 className="text-warning-dark">
                                        {filteredData.length > 0 ? new Date(filteredData[0].payment_date).toLocaleDateString('th-TH') : '-'}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </CardComponent>
                </div>
            </div>
        </Layout>
    );
}
